import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { RefreshCcw } from "lucide-react";
import { toast } from "sonner";

interface LogRow {
  id: string;
  notification_id: string | null;
  user_id: string;
  subscription_id: string | null;
  endpoint: string | null;
  status: string;
  status_code: number | null;
  error: string | null;
  notification_type: string | null;
  notification_title: string | null;
  created_at: string;
}

interface ProfileRow { id: string; name: string | null; email: string | null }

const PAGE_SIZE = 100;

const statusVariant = (s: string): "default" | "secondary" | "destructive" | "outline" => {
  if (s === "sent") return "default";
  if (s === "failed") return "destructive";
  if (s === "removed") return "outline";
  return "secondary";
};

const endpointHost = (url: string | null) => {
  if (!url) return "—";
  try { return new URL(url).host; } catch { return url.slice(0, 32); }
};

const fmt = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("cs-CZ", { dateStyle: "short", timeStyle: "medium" });
};

export const PushDeliveryLogTab = () => {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileRow>>({});
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);

  const load = async () => {
    setLoading(true);
    try {
      let q = supabase
        .from("push_delivery_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (statusFilter !== "all") q = q.eq("status", statusFilter);
      const { data, error } = await q;
      if (error) throw error;
      const list = (data ?? []) as LogRow[];
      setRows(list);
      const userIds = Array.from(new Set(list.map((r) => r.user_id)));
      if (userIds.length > 0) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id,name,email")
          .in("id", userIds);
        const map: Record<string, ProfileRow> = {};
        for (const p of (profs ?? []) as ProfileRow[]) map[p.id] = p;
        setProfiles(map);
      } else {
        setProfiles({});
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Chyba při načítání logu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, limit]);

  const visible = rows.filter((r) => {
    if (!search.trim()) return true;
    const p = profiles[r.user_id];
    const hay = `${p?.name ?? ""} ${p?.email ?? ""} ${r.notification_title ?? ""}`.toLowerCase();
    return hay.includes(search.trim().toLowerCase());
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          <span>Push log</span>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            <RefreshCcw className="h-4 w-4 mr-1" />Obnovit
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Všechny stavy</SelectItem>
              <SelectItem value="sent">sent</SelectItem>
              <SelectItem value="failed">failed</SelectItem>
              <SelectItem value="removed">removed</SelectItem>
              <SelectItem value="skipped_no_subscriptions">skipped (no subs)</SelectItem>
            </SelectContent>
          </Select>
          <Input
            className="max-w-[260px]"
            placeholder="Hledat uživatele / titulek…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="text-xs text-muted-foreground ml-auto">
            {visible.length} z {rows.length} (limit {limit})
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Čas</th>
                <th className="py-2 pr-3">Uživatel</th>
                <th className="py-2 pr-3">Typ</th>
                <th className="py-2 pr-3">Titulek</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Code</th>
                <th className="py-2 pr-3">Endpoint</th>
                <th className="py-2 pr-3">Chyba</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const p = profiles[r.user_id];
                return (
                  <tr key={r.id} className="border-t border-border/40 align-top">
                    <td className="py-2 pr-3 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{p?.name ?? "—"}</div>
                      <div className="text-xs text-muted-foreground">{p?.email ?? r.user_id.slice(0, 8)}</div>
                    </td>
                    <td className="py-2 pr-3">{r.notification_type ?? "—"}</td>
                    <td className="py-2 pr-3 max-w-[220px] truncate" title={r.notification_title ?? ""}>
                      {r.notification_title ?? "—"}
                    </td>
                    <td className="py-2 pr-3"><Badge variant={statusVariant(r.status)}>{r.status}</Badge></td>
                    <td className="py-2 pr-3">{r.status_code ?? "—"}</td>
                    <td className="py-2 pr-3 text-xs" title={r.endpoint ?? ""}>{endpointHost(r.endpoint)}</td>
                    <td className="py-2 pr-3 text-xs text-destructive max-w-[280px] truncate" title={r.error ?? ""}>
                      {r.error ?? ""}
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && !loading && (
                <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Žádné záznamy</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {rows.length >= limit && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={() => setLimit((l) => l + PAGE_SIZE)} disabled={loading}>
              Načíst další {PAGE_SIZE}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
